import { useCallback, useMemo, useState } from 'react'
import View from './MyCollection.view'
import { MyCollectionLogicProps } from './MyCollection.types'
import { useHistory } from 'react-router-dom'
import {
  TO_COLLECTION_ITEM_DETAIL,
  TO_DROP_OPENING,
  TO_DROP_PENDING_PAYMENT,
  TO_GALLERY,
  TO_GALLERY_DETAIL,
  TO_MARKETPLACE_OFFER_DETAILS,
  TO_MARKETPLACE_SELL,
  TO_TRACKING_PAGE,
} from 'core/modules/router'
import { useGalleries } from 'core/logic/gallery/gallery.hook'
import { useUpToDateMyCollectionItems } from 'context/MyCollection'
import { BaseAssetType, OwnOfferAsset } from 'core/logic/asset/asset.types'
import { usePurchases } from 'core/logic/purchase/purchase.hook'
import { DeliveryType } from 'core/logic/delivery/delivery.types'
import { PendingPurchase } from 'core/logic/purchase/purchase.types'
import { useDynamicMarketplaceOffers } from 'context/Marketplace'
import { useAssets } from 'core/logic/asset/asset.hook'
import { preffixDraft } from 'pages/Authenticated/Marketplace/CreateOffer/OfferType/DigitalTwin/DigitalTwin.logic'
import { DraftShape } from 'pages/Authenticated/Marketplace/CreateOffer/OfferType/DigitalTwin/DigitalTwin.types'
import {
  Filter,
  MarketplaceFilter,
} from 'components/AssetFilters/AssetFilters.types'
import { useDeliveries as useAllDeliveries } from 'core/logic/delivery/delivery.hook'
import { DELIVERY_STATUSES, useDeliveries } from 'context/Delivery'
import { useUser } from 'context/User'

const MY_OFFERS_FILTER = { byUser: true } as Filter
const MY_SOLD_SALES_FILTER = { byUser: true, sold: true } as MarketplaceFilter

export default function MyCollectionLogic(props: MyCollectionLogicProps) {
  const { detail } = useUser()
  const history = useHistory()
  const onClickAsset = useCallback(
    (asset: BaseAssetType | DeliveryType | PendingPurchase | OwnOfferAsset) => {
      if ('logistics' in asset && !!asset.logistics) {
        return history.push(
          TO_GALLERY(asset.sellOrder?.galleryId!)(TO_TRACKING_PAGE(asset.id))
        )
      }
      if ('offerResume' in asset && !!asset.offerResume) {
        return history.push(
          TO_GALLERY(asset.galleryId!)(
            TO_MARKETPLACE_OFFER_DETAILS(
              asset.id,
              asset.offerResume.offerId,
              asset.tokenId
            )
          )
        )
      }
      if (
        'status' in asset &&
        DELIVERY_STATUSES.includes(asset.status as DeliveryType['status'])
      )
        history.push(TO_TRACKING_PAGE(asset.id))
      if ('paymentStatus' in asset)
        if (asset.nextAction?.data.galleryId)
          history.push(
            TO_GALLERY(asset.nextAction?.data.galleryId)(
              TO_DROP_PENDING_PAYMENT(asset.id)
            )
          )
        else history.push(TO_DROP_PENDING_PAYMENT(asset.id))
      else if ('type' in asset && asset.type === 'airdrop')
        history.push(TO_DROP_OPENING(asset.asset!.id))
      else if ('purchaseId' in asset) history.push(TO_DROP_OPENING(asset.id))
      else if (asset.galleryId)
        history.push(
          TO_GALLERY(asset.galleryId)(
            TO_COLLECTION_ITEM_DETAIL(asset.id, asset.tokenId)
          )
        )
      else history.push(TO_COLLECTION_ITEM_DETAIL(asset.id, asset.tokenId))
    },
    [history]
  )
  const { galleries } = useGalleries()
  const { ownedItems } = useUpToDateMyCollectionItems()
  const { deliveries } = useAllDeliveries()
  const { purchases: pending } = usePurchases()
  const { sales } = useDynamicMarketplaceOffers(MY_OFFERS_FILTER)
  const { assets } = useAssets()
  const loadDrafts = () => {
    const allLSKeys = Object.keys(localStorage)
    const draftKeys = allLSKeys.filter((k) => k.startsWith(preffixDraft('')))
    return draftKeys.map((k) => {
      const base = JSON.parse(localStorage.getItem(k)!) as DraftShape
      const [tokenId, assetId] = k.split('-')
      return {
        ...base,
        assetId: assetId,
        draftId: k,
        tokenId: Number(tokenId),
      }
    })
  }
  const [drafts, setDrafts] = useState(loadDrafts)

  const { sales: salesImBuyerOrSeller } =
    useDynamicMarketplaceOffers(MY_SOLD_SALES_FILTER)
  const relatedDeliveriesIds = useMemo(
    () =>
      salesImBuyerOrSeller.items
        ?.map((d) => d.deliveryId)
        .filter(Boolean) as string[],
    [salesImBuyerOrSeller.items]
  )
  const { entities } = useDeliveries(relatedDeliveriesIds)

  const purchasesToExpire = useMemo(
    () => pending?.filter((p) => p.expireAt.getTime() > new Date().getTime()),
    [pending]
  )

  const mySoldItems = salesImBuyerOrSeller.items?.filter(
    (s) => s.user === detail!.uid
  )

  return (
    <View
      {...props}
      galleries={galleries}
      onOpenGallery={(g) => {
        history.push(TO_GALLERY_DETAIL(g.id))
      }}
      onClickAsset={onClickAsset}
      openedCollection={ownedItems}
      closedCollection={deliveries}
      dynamicSales={sales}
      soldSales={{
        ...salesImBuyerOrSeller,
        items: mySoldItems,
        totalItems: () => mySoldItems?.length,
      }}
      deliveries={entities}
      assets={assets || undefined}
      drafts={drafts}
      onOpenDraft={(draft, asset) =>
        history.push(
          TO_GALLERY(asset.galleryId!)(
            TO_MARKETPLACE_SELL(draft.assetId, draft.tokenId)
          )
        )
      }
      onOpenMarketplaceAsset={(s, asset) =>
        history.push(
          TO_GALLERY(asset.galleryId!)(
            TO_MARKETPLACE_OFFER_DETAILS(asset.id, s.offerId, asset.tokenId)
          )
        )
      }
      onRemoveDraft={(d) => {
        localStorage.removeItem(d.draftId)
        setDrafts(() => loadDrafts())
      }}
      onOpenDelivery={(a, d) => {
        history.push(TO_GALLERY(a.galleryId!)(TO_TRACKING_PAGE(d.id!)))
      }}
      pending={purchasesToExpire}
    />
  )
}
